import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, withDirectives as _withDirectives, renderSlot as _renderSlot, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = ["aria-label", "aria-busy"];
const _hoisted_2 = ["src"];
const _hoisted_3 = { class: "transcript-chat-message__bubble" };
const _hoisted_4 = { class: "transcript-chat-message__speaker" };
const _hoisted_5 = ["href"];
const _hoisted_6 = {
    key: 2,
    class: "sr-only"
};
const _hoisted_7 = ["aria-label"];
const _hoisted_8 = ["aria-label"];
const _hoisted_9 = {
    key: 0,
    class: "transcript-chat-message__regenerating"
};
const _hoisted_10 = ["data-segment-id"];
const _hoisted_11 = ["innerHTML"];
const _hoisted_12 = ["start", "end", "data-editable", "data-word-id", "tabindex", "onClick", "onDblclick", "onFocus", "onKeydown"];
const _hoisted_13 = {
    key: 4,
    class: "rec-dropdown transcript-chat-message__segment-menu"
};
const _hoisted_14 = ["aria-label", "onFocus", "onPointerdown"];
import { computed, inject, nextTick, onBeforeUnmount, ref, shallowRef, useSlots, useTemplateRef } from "vue";
import { useEventListener } from "@vueuse/core";
import { formatDuration } from "@/common/helpers/format";
import { useTranscriptEditContext } from "@/common/injection-keys/transcript-edit-context";
import { useTranscriptWordElementsContext } from "@/common/injection-keys/transcript-word-elements";
import { useAnnouncer } from "@/common/composables/announcer";
import { useRamiusExcerptKeyboard } from "@/common/composables/ramius-excerpt-keyboard";
import { vTooltip } from "@/common/directives/tip-directive";
// Components
import DropdownMenu from "@/common/components/DropdownMenu.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
import WordInlineEditor from "@/common/components/transcription-layout/WordInlineEditor.vue";
import TranscriptAssets from "@/common/components/transcription-layout/TranscriptAssets.vue";
import TranscriptPoll from "@/common/components/transcription-layout/TranscriptPoll.vue";
import LoadingDots from "@/common/components/LoadingDots.vue";
import { TranscriptSource, WordEditAdvance } from "@/types/transcript";
const SEEK_DEFER_MS = 350;
const PRIMARY_POINTER_BUTTON = 0;
export default /*@__PURE__*/ _defineComponent({
    __name: 'TranscriptMessage',
    props: {
        segments: {},
        speakerDisplayName: {},
        speakerColor: {},
        isHost: { type: Boolean },
        speakerLabel: {},
        editable: { type: Boolean },
        disableSpeakerMenuOptions: { type: Boolean },
        disableTextHover: { type: Boolean },
        disableWordHoverUnderline: { type: Boolean },
        circularAvatar: { type: Boolean },
        segmentTextAttrs: {},
        showChatAffordances: { type: Boolean },
        speakerProfileUrl: {},
        speakerProfilePhoto: {},
        speakerInactive: { type: Boolean },
        regenerating: { type: Boolean },
        segmentEnd: {},
        hasSpeakerMenu: { type: Boolean, default: undefined },
        hasSegmentMenu: { type: Boolean, default: undefined },
        focusAreaKey: {}
    },
    emits: ["seek", "edit-segment-text"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const i18n = inject("i18n");
        const { announce } = useAnnouncer();
        // Active inline editor — at most one across the whole message. Identified by
        // segment id + word index so the matching <span> can be hidden while the
        // <input> is mounted in its place.
        const editing = ref(null);
        // Bumped on every (re)open so the editor remounts fresh even when the next
        // target index equals the just-committed one (delete-forward) — otherwise Vue
        // reuses the stale, already-committed instance and the input freezes.
        const editSession = ref(0);
        const startWordEdit = (segmentId, wordIndex, selectOnMount = false) => {
            // Chat/backroom bubbles aren't transcript words — not editable (legacy parity).
            if (!props.editable || isChatSourced.value)
                return;
            // A single click may have queued a deferred seek; opening the editor (E or
            // double-click) must cancel it so playback can't jump mid-edit.
            clearPendingSeek();
            editSession.value++;
            editing.value = { segmentId, wordIndex, selectOnMount };
        };
        // Pending seek emission queued by a single click in editable mode. Cleared
        // when an edit starts (so opening the editor never seeks) and on component
        // unmount (so we never emit `seek` after teardown).
        let pendingSeekTimer = null;
        const clearPendingSeek = () => {
            if (pendingSeekTimer) {
                clearTimeout(pendingSeekTimer);
                pendingSeekTimer = null;
            }
        };
        // Escape-refocus macrotask; tracked so onBeforeUnmount can cancel it before the chevron detaches.
        let pendingChevronFocusTimer = null;
        const onWordClick = (event, start) => {
            if (!props.editable) {
                emit("seek", start);
                return;
            }
            // 2nd click of a dblclick gesture — the pending seek from the 1st click
            // will be cancelled by the dblclick handler that follows.
            if (event.detail >= 2)
                return;
            clearPendingSeek();
            pendingSeekTimer = setTimeout(() => {
                pendingSeekTimer = null;
                emit("seek", start);
            }, SEEK_DEFER_MS);
        };
        const onWordDblclick = (segmentId, wordIndex) => {
            startWordEdit(segmentId, wordIndex, true);
        };
        onBeforeUnmount(() => {
            clearPendingSeek();
            if (pendingChevronFocusTimer)
                clearTimeout(pendingChevronFocusTimer);
        });
        const messageSegmentIds = computed(() => props.segments.map((segment) => segment.id));
        const slots = useSlots();
        const showSpeakerMenu = computed(() => props.hasSpeakerMenu ?? !!slots["speaker-menu"]);
        const showSegmentMenu = computed(() => props.hasSegmentMenu ?? !!slots["segment-menu"]);
        // Only the rendered window; every other derivation stays on the full `segments` prop.
        const visibleSegments = computed(() => props.segments.slice(0, props.segmentEnd ?? props.segments.length));
        const armedSegmentMenus = ref(new Set());
        // The press that armed a menu; its release decides whether the menu opens, so the user can still abort.
        const pendingMenuPress = shallowRef(null);
        const onSegmentMenuPressEnd = (event) => {
            const pending = pendingMenuPress.value;
            if (!pending || event.pointerId !== pending.pointerId)
                return;
            pendingMenuPress.value = null;
            // A cancelled press (touch turned scroll) or a release off the trigger aborts instead of opening.
            if (event.type !== "pointerup")
                return;
            const menuButton = segmentMenus.get(pending.segmentId)?.menuButton;
            if (!menuButton || !(event.target instanceof Node) || !menuButton.contains(event.target))
                return;
            // The placeholder detached before the browser could dispatch a click, so the real trigger needs one.
            menuButton.click();
        };
        // A pending press is the only thing that binds these; clearing it unbinds, and scope teardown covers unmount.
        useEventListener(() => (pendingMenuPress.value ? window : null), ["pointerup", "pointercancel"], onSegmentMenuPressEnd);
        const armSegmentMenu = (segmentId, pressEvent) => {
            if (pressEvent && pressEvent.button !== PRIMARY_POINTER_BUTTON)
                return;
            // Already armed: the real DropdownMenu owns its own clicks, so no gesture tracking is needed.
            if (armedSegmentMenus.value.has(segmentId))
                return;
            if (pressEvent)
                pendingMenuPress.value = { segmentId, pointerId: pressEvent.pointerId };
            armedSegmentMenus.value = new Set(armedSegmentMenus.value).add(segmentId);
            void nextTick(() => {
                // preventScroll: the swap replaces the focused node in place, so scrolling it into view would jump the page.
                segmentMenus.get(segmentId)?.menuButton?.focus({ preventScroll: true });
            });
        };
        // Chat-source affordances key off the bubble's source (uniform per group).
        const messageSource = computed(() => props.segments[0]?.source ?? TranscriptSource.Spoken);
        const isChatSourced = computed(() => props.showChatAffordances && messageSource.value !== TranscriptSource.Spoken);
        const isBackroom = computed(() => messageSource.value === TranscriptSource.Backroom);
        // Whether this bubble's speaker links to a profile card. Data-driven (any caller supplying a
        // profile URL — EC attribution + IDI), independent of the IDI-only chat affordances.
        const hasProfileLink = computed(() => !!props.speakerProfileUrl);
        // SR/role label for a chat-sourced bubble (lowercase; folds into the message aria-label).
        const sourceLabel = computed(() => (isBackroom.value ? i18n.t("backroom comment") : i18n.t("in chat message")));
        // Visible keyboard-icon tooltip + its aria-label (title-case).
        const chatIconLabel = computed(() => (isBackroom.value ? i18n.t("Backroom comment") : i18n.t("Sent in chat")));
        // Composed accessible name for the message-level tab stop: name, role, timestamp,
        // quoted text (External Content Phase-2 order). Backroom/in-chat replace the role slot.
        const messageRole = computed(() => {
            if (isChatSourced.value)
                return sourceLabel.value;
            return props.isHost ? i18n.t("Host") : i18n.t("Participant");
        });
        // Drop empty segment texts (asset/poll-only chat rows) so the aria-label has no stray whitespace.
        const quotedText = computed(() => props.segments
            .map((segment) => segment.text.trim())
            .filter((text) => text.length > 0)
            .join(" "));
        const messageAnnouncement = computed(() => {
            // While regenerating the words are hidden behind the loader, so drop the stale quoted text
            const base = [props.speakerDisplayName, messageRole.value, formatDuration(props.segments[0]?.start ?? 0)];
            return (props.regenerating ? base : [...base, quotedText.value])
                .filter((part) => part && String(part).length > 0)
                .join(", ");
        });
        // Groups the rendered (progressive-reveal-aware) segments into focus-area units; no key → one inert
        // pass-through wrapper, so the default DOM keeps its per-segment areas untouched. Empty while
        // regenerating, where the loader renders in place of the segments.
        const segmentUnits = computed(() => {
            if (props.regenerating)
                return [];
            const segments = visibleSegments.value;
            if (segments.length === 0)
                return [];
            if (!props.focusAreaKey)
                return [segments];
            const units = [];
            let lastKey;
            for (const segment of segments) {
                const key = props.focusAreaKey(segment);
                const currentUnit = units[units.length - 1];
                if (currentUnit && key === lastKey)
                    currentUnit.push(segment);
                else
                    units.push([segment]);
                lastKey = key;
            }
            return units;
        });
        // Message-relative position for slot payloads; the unit loop's inner index is unit-relative.
        const segmentIndexById = computed(() => new Map(props.segments.map((segment, index) => [segment.id, index])));
        // DropdownMenu blurs its trigger on close; refocus the chevron so focus isn't lost.
        const closeSpeakerMenu = (toggleMenu) => {
            toggleMenu?.(false);
            restoreFocusToChevron("spk");
        };
        const closeSegmentMenu = (toggleMenu, segmentId) => {
            toggleMenu?.(false);
            if (segmentId !== undefined)
                restoreFocusToChevron(`seg:${segmentId}`);
        };
        // The hosting view provides the live char-range map; the store action that
        // updates it is owned by the parent because it is coupled to the commit +
        // Undo toast lifecycle. The fallback (no provider) yields an empty Map so
        // this component stays usable outside that view.
        const { editedRangesBySegment } = useTranscriptEditContext();
        // Map char ranges onto word indices given a single-space-joined word array.
        // Returns the Set of word indices whose char span overlaps any range.
        const mapRangesToWordIndices = (ranges, words) => {
            const indices = new Set();
            if (ranges.length === 0)
                return indices;
            let offset = 0;
            for (let i = 0; i < words.length; i++) {
                const wordStart = offset;
                const wordEnd = offset + words[i].length;
                for (const range of ranges) {
                    if (range.end > wordStart && range.start < wordEnd) {
                        indices.add(i);
                        break;
                    }
                }
                offset = wordEnd + 1;
            }
            return indices;
        };
        // Project the stored char ranges onto the rendered word arrays. Recomputes
        // only when ranges or segments change.
        const editedIndicesBySegment = computed(() => {
            const result = new Map();
            for (const segment of props.segments) {
                const ranges = editedRangesBySegment.value.get(segment.id);
                if (!ranges || ranges.length === 0)
                    continue;
                const words = segment.words.map((chunk) => chunk.word);
                result.set(segment.id, mapRangesToWordIndices(ranges, words));
            }
            return result;
        });
        const onCommit = (segment, payload) => {
            // Assemble the full segment text by replacing the targeted word position
            // with the user's tokens. Substitute = 1 token; delete = 0 tokens; insert
            // = N tokens. The BE re-tokenises on whitespace.
            const newWords = segment.words.map((w) => w.word);
            newWords.splice(payload.wordIndex, 1, ...payload.tokens);
            const fullText = newWords.filter((s) => s.length > 0).join(" ");
            const previousText = segment.text;
            // Directional advance across segments WITHIN this message (no wrap, no cross-message).
            // Forward: next word in the segment (delete keeps the index as the next word shifts in,
            // substitute/insert skips past inserted tokens); at the segment end, spill into the next
            // non-empty segment's first word. Backward: previous slot; at index 0, the previous
            // non-empty segment's last word. None at the message edge → close.
            const segmentIndex = props.segments.findIndex((seg) => seg.id === segment.id);
            const segmentLength = segment.words.length;
            const insertCount = payload.tokens.length;
            let target = null;
            if (payload.advance === WordEditAdvance.Forward) {
                if (payload.wordIndex < segmentLength - 1) {
                    target = { segmentId: segment.id, wordIndex: payload.wordIndex + insertCount };
                }
                else {
                    for (let next = segmentIndex + 1; next < props.segments.length; next++) {
                        if (props.segments[next].words.length > 0) {
                            target = { segmentId: props.segments[next].id, wordIndex: 0 };
                            break;
                        }
                    }
                }
            }
            else if (payload.advance === WordEditAdvance.Backward) {
                if (payload.wordIndex > 0) {
                    target = { segmentId: segment.id, wordIndex: payload.wordIndex - 1 };
                }
                else {
                    for (let prev = segmentIndex - 1; prev >= 0; prev--) {
                        const prevWords = props.segments[prev].words;
                        if (prevWords.length > 0) {
                            target = { segmentId: props.segments[prev].id, wordIndex: prevWords.length - 1 };
                            break;
                        }
                    }
                }
            }
            // Bump so a delete-forward (target index === edited index) remounts fresh, not the stale instance.
            if (target)
                editSession.value++;
            // Tab-advance is a keyboard move, so the remounted editor keeps the caret at the end like the E entry point.
            editing.value = target ? { ...target, selectOnMount: false } : null;
            // Skip the upward save when the user didn't actually change the word —
            // the BE's stored text frequently doesn't match a whitespace-rejoined
            // `segment.words`, so the view's text-equality guard can't catch this
            // on its own. The advance signal above still fires either way.
            if (!payload.unchanged) {
                emit("edit-segment-text", segment.id, payload.wordIndex, payload.tokens, fullText, previousText, payload.advance);
            }
            // When the editor closes (Enter, blur, Tab/Shift+Tab past either end),
            // focus would otherwise drop to document.body. The success-path toast
            // button focuses async; the no-op + failure paths never run it. Restore
            // focus to the just-edited span as a stable floor — the toast still
            // wins on success.
            if (target === null) {
                const restoreKey = `${segment.id}:${payload.wordIndex}`;
                void nextTick(() => {
                    // Only restore if focus actually dropped to body — if the user has
                    // already moved focus elsewhere (clicked, tabbed, toast stole it),
                    // don't yank it back.
                    if (document.activeElement && document.activeElement !== document.body)
                        return;
                    // Recompute against the post-update render: an optimistic delete/insert
                    // may have shifted or removed this word's slot. When the slot is gone
                    // (delete of the last word), fall back to the new last word, and clamp
                    // so we never focus a removed (detached) index.
                    const total = totalWords.value;
                    if (total === 0)
                        return;
                    const mapped = flatIndex.value.get(restoreKey);
                    const restoreIdx = Math.min(mapped ?? total - 1, total - 1);
                    focusedFlatIndex.value = restoreIdx;
                    wordEls.value[restoreIdx]?.focus();
                });
            }
        };
        const onCancel = () => {
            const target = editing.value;
            editing.value = null;
            if (!target)
                return;
            const flatIdx = flatIndex.value.get(`${target.segmentId}:${target.wordIndex}`);
            if (flatIdx == null)
                return;
            // The <input> is unmounting; without restoration focus falls to document.body
            // and keyboard users lose all context.
            void nextTick(() => {
                focusedFlatIndex.value = flatIdx;
                wordEls.value[flatIdx]?.focus();
            });
        };
        // Flat word cursor for the edit/commit/restore flow; wordEls is indexed by it.
        const focusedFlatIndex = ref(0);
        const wordEls = ref([]);
        const rootEl = useTemplateRef("rootEl");
        // Chevron focus-restore reads each DropdownMenu's exposed `menuButton` instead of
        // querying for its internal trigger class. Segment menus are keyed by segment id.
        const speakerMenu = useTemplateRef("speakerMenu");
        const segmentMenus = new Map();
        const registerSegmentMenu = (segmentId, instance) => {
            if (instance)
                segmentMenus.set(segmentId, instance);
            else
                segmentMenus.delete(segmentId);
        };
        // Active (tabindex=0) word per segment; reassign (don't mutate) so it stays reactive.
        const activeWordBySegment = ref({});
        const activeWordOf = (segmentId) => activeWordBySegment.value[segmentId] ?? 0;
        const setActiveWord = (segmentId, wordIndex) => {
            activeWordBySegment.value = { ...activeWordBySegment.value, [segmentId]: wordIndex };
        };
        // Chevron stop id ('spk' | 'seg:<id>') → trigger button, for focus restore on close.
        const chevronElById = (id) => {
            if (id === "spk")
                return speakerMenu.value?.menuButton ?? null;
            if (id.startsWith("seg:"))
                return segmentMenus.get(Number(id.slice(4)))?.menuButton ?? null;
            return null;
        };
        const restoreFocusToChevron = (id) => {
            void nextTick(() => chevronElById(id)?.focus());
        };
        // Flat index per (segment, wordIndex) — drives tabindex and arrow handlers.
        const flatIndex = computed(() => {
            const result = new Map();
            let n = 0;
            for (const seg of props.segments) {
                for (let i = 0; i < seg.words.length; i++) {
                    result.set(`${seg.id}:${i}`, n++);
                }
            }
            return result;
        });
        const totalWords = computed(() => {
            let n = 0;
            for (const seg of props.segments)
                n += seg.words.length;
            return n;
        });
        const { register: registerWordElementRef } = useTranscriptWordElementsContext();
        const registerWordEl = (segment, i, el) => {
            const wordKey = `${segment.id}:${i}`;
            const flatIdx = flatIndex.value.get(wordKey);
            if (flatIdx != null && el instanceof HTMLSpanElement)
                wordEls.value[flatIdx] = el;
            registerWordElementRef(wordKey, el instanceof HTMLElement ? el : null);
        };
        const wordElementAt = (segmentId, wordIndex) => {
            const flatIdx = flatIndex.value.get(`${segmentId}:${wordIndex}`);
            return flatIdx != null ? wordEls.value[flatIdx] ?? null : null;
        };
        const { isAvailable: canExcerpt, isActive: hasExcerptSelection, anchorWordIndex: excerptAnchorIndex, activeSegmentId: excerptSegmentId, selectionBounds: excerptSelectionBounds, extendBy: extendExcerpt, clear: clearExcerptSelection, commit: commitExcerpt, moveMenuFocus, isSaveControlFocused } = useRamiusExcerptKeyboard({ wordElementAt });
        // Full key guidance once per message; later moves announce position only, so arrowing is not a wall of speech.
        const hasAnnouncedWordKeys = ref(false);
        const announceExcerptChange = (segment, change) => {
            const chunk = segment.words[change.changedWordIndex]?.word.trim() ?? "";
            announce(
            // Chunks usually end in their own punctuation, so the count goes in parentheses rather than after a period.
            change.grew
                ? i18n.t("Added {0} ({1} word(s) selected for excerpt)", [chunk, change.wordCount])
                : i18n.t("Removed {0} ({1} word(s) selected for excerpt)", [chunk, change.wordCount]));
        };
        // Restoring focus re-announces the word position, which would otherwise bury the saved/cleared message.
        const skipNextWordAnnounce = ref(false);
        // Also moves the roving tabindex, so plain Tab resumes from this word rather than the menu.
        const moveFocusToWord = (segmentId, wordIndex) => {
            setActiveWord(segmentId, wordIndex);
            void nextTick(() => wordElementAt(segmentId, wordIndex)?.focus());
        };
        // Focus is inside the engine's menu by then, so it has to be handed back to a word either way.
        const returnFocusToAnchor = (segmentId, wordIndex) => {
            skipNextWordAnnounce.value = true;
            moveFocusToWord(segmentId, wordIndex);
        };
        const onExcerptMenuKeydown = (event, segment) => {
            const returnIndex = excerptAnchorIndex.value ?? 0;
            const key = event.key;
            if (key === "Escape" || event.keyCode === 27) {
                event.preventDefault();
                clearExcerptSelection();
                window.getSelection()?.removeAllRanges();
                announce(i18n.t("Excerpt selection cleared."));
                returnFocusToAnchor(segment.id, returnIndex);
                return;
            }
            // Arrows walk the menu, so Tab means leave it — to the chunk just outside the range, never to the footer links the menu sits among.
            if (key === "Tab" || event.keyCode === 9) {
                event.preventDefault();
                const bounds = excerptSelectionBounds.value;
                const target = event.shiftKey
                    ? Math.max(0, (bounds?.first ?? returnIndex) - 1)
                    : Math.min(segment.words.length - 1, (bounds?.last ?? returnIndex) + 1);
                clearExcerptSelection();
                window.getSelection()?.removeAllRanges();
                moveFocusToWord(segment.id, target);
                return;
            }
            // Focus sits in the menu from the first extension onward, so this is where further growing happens.
            const isRight = key === "ArrowRight" || event.keyCode === 39;
            const isLeft = key === "ArrowLeft" || event.keyCode === 37;
            if ((isRight || isLeft) && event.shiftKey) {
                event.preventDefault();
                const change = extendExcerpt(segment.id, returnIndex, isRight ? 1 : -1, segment.words.length);
                if (change)
                    announceExcerptChange(segment, change);
                return;
            }
            // The engine's own S handler covers that shortcut; Enter and Space need routing because the save control is a bare anchor.
            const isActivate = key === "Enter" || key === " " || event.keyCode === 13 || event.keyCode === 32;
            if (isActivate && isSaveControlFocused()) {
                event.preventDefault();
                const saved = commitExcerpt();
                announce(saved ? i18n.t("Excerpt saved.") : i18n.t("That selection could not be saved."));
                if (saved)
                    returnFocusToAnchor(segment.id, returnIndex);
                return;
            }
            const isDown = key === "ArrowDown" || event.keyCode === 40;
            const isUp = key === "ArrowUp" || event.keyCode === 38;
            if ((isDown || isUp) && !event.shiftKey) {
                event.preventDefault();
                moveMenuFocus(isDown ? 1 : -1);
            }
        };
        // The confirm menu is a single global element, so only the message holding the selection may bind to it.
        const excerptMenuEl = computed(() => {
            if (excerptSegmentId.value === null)
                return null;
            const id = window.Ramius?.highlight?.confirmId;
            return id ? document.getElementById(id) : null;
        });
        useEventListener(excerptMenuEl, "keydown", (event) => {
            const segment = props.segments.find((candidate) => candidate.id === excerptSegmentId.value);
            if (segment)
                onExcerptMenuKeydown(event, segment);
        });
        const onWordFocus = (segment, wordIndex) => {
            setActiveWord(segment.id, wordIndex);
            focusedFlatIndex.value = flatIndex.value.get(`${segment.id}:${wordIndex}`) ?? 0;
            if (skipNextWordAnnounce.value) {
                skipNextWordAnnounce.value = false;
                return;
            }
            // Arrows move focus, so repeating the key guidance on every word would bury the text itself.
            if (hasAnnouncedWordKeys.value) {
                announce(i18n.t("Word {0} of {1}", [wordIndex + 1, segment.words.length]));
                return;
            }
            hasAnnouncedWordKeys.value = true;
            // Position + word-nav instructions for screen readers (the "E to edit" clause
            // only when the transcript is editable). Arrows rove within the segment, so the
            // count is segment-scoped.
            const instructions = props.editable
                ? i18n.t("Word {0} of {1}: Use left and right arrows to move between words. Press Enter to play from this word. Press E to edit the transcript.", [wordIndex + 1, segment.words.length])
                : i18n.t("Word {0} of {1}: Use left and right arrows to move between words. Press Enter to play from this word.", [
                    wordIndex + 1,
                    segment.words.length
                ]);
            const excerptHint = canExcerpt()
                ? ` ${i18n.t("Hold Shift with the arrows to select text for an excerpt; the excerpt menu opens as you select.")}`
                : "";
            announce(instructions + excerptHint);
        };
        // Escape: refocus the chevron after DropdownMenu's blur-on-close (deferred a macrotask), unless a nested flyout is still open.
        const onRootKeydownCapture = (event) => {
            if (editing.value)
                return;
            const isEscape = event.key === "Escape" || event.code === "Escape" || event.keyCode === 27;
            if (!isEscape)
                return;
            const openDropdown = rootEl.value?.querySelector(".rec-dropdown--open");
            const chevron = openDropdown?.querySelector(".rec-dropdown__button") ?? null;
            if (!chevron)
                return;
            if (pendingChevronFocusTimer)
                clearTimeout(pendingChevronFocusTimer);
            pendingChevronFocusTimer = setTimeout(() => {
                pendingChevronFocusTimer = null;
                if (rootEl.value && !rootEl.value.querySelector(".rec-dropdown--open"))
                    chevron.focus();
            }, 0);
        };
        // String style lets Vue short-circuit re-patching when timings are unchanged.
        const wordStyle = (chunk) => `--start:${chunk.start};--end:${chunk.end}`;
        const onWordKeydown = (event, segment, wordIndex, start) => {
            // Bare-key shortcuts only — Cmd/Ctrl/Alt sequences belong to the browser
            // (back-nav, line-start, Edit menu, etc.) and must not be hijacked.
            if (event.ctrlKey || event.metaKey || event.altKey)
                return;
            // Match by `key`, `code`, or legacy `keyCode` so vue-test-utils' keydown
            // modifier (which sets keyCode only) works alongside real browser events.
            const isLeft = event.key === "ArrowLeft" || event.code === "ArrowLeft" || event.keyCode === 37;
            const isRight = event.key === "ArrowRight" || event.code === "ArrowRight" || event.keyCode === 39;
            const isEnter = event.key === "Enter" || event.code === "Enter" || event.keyCode === 13;
            const isSpace = event.key === " " || event.code === "Space" || event.keyCode === 32;
            const isE = event.key === "e" || event.key === "E" || event.code === "KeyE" || event.keyCode === 69;
            const isEscape = event.key === "Escape" || event.code === "Escape" || event.keyCode === 27;
            if ((isLeft || isRight) && event.shiftKey && canExcerpt()) {
                // Shift was previously ignored here, so this only repurposes an arrow that already just moved focus.
                event.preventDefault();
                const change = extendExcerpt(segment.id, wordIndex, isRight ? 1 : -1, segment.words.length);
                if (change)
                    announceExcerptChange(segment, change);
                return;
            }
            if (isEscape && hasExcerptSelection.value) {
                // Stop here so the message-level handler does not also treat this as a menu dismissal.
                event.stopPropagation();
                clearExcerptSelection();
                window.getSelection()?.removeAllRanges();
                announce(i18n.t("Excerpt selection cleared."));
                return;
            }
            if (isLeft || isRight) {
                // Arrows stay within the segment (clamp at ends); Tab crosses segments/chevrons.
                event.preventDefault();
                // A bare arrow abandons any pending excerpt range, matching how a plain arrow drops a text selection.
                clearExcerptSelection();
                const nextIndex = wordIndex + (isRight ? 1 : -1);
                if (nextIndex < 0 || nextIndex >= segment.words.length)
                    return;
                setActiveWord(segment.id, nextIndex);
                const flatIdx = flatIndex.value.get(`${segment.id}:${nextIndex}`);
                void nextTick(() => {
                    if (flatIdx != null)
                        wordEls.value[flatIdx]?.focus();
                });
            }
            else if (isE && props.editable) {
                event.preventDefault();
                startWordEdit(segment.id, wordIndex);
            }
            else if (isEnter || isSpace) {
                event.preventDefault();
                emit("seek", start);
            }
        };
        // Tab can land on a segment's chevron (a natural tab stop) where DropdownMenu swallows the arrows.
        // Capture ArrowLeft before that bubble listener and re-enter the segment's words so the user can
        // keep arrowing and press E — otherwise later segments' words are unreachable by keyboard.
        const onMessageKeydownCapture = (event) => {
            if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)
                return;
            const isLeft = event.key === "ArrowLeft" || event.code === "ArrowLeft" || event.keyCode === 37;
            if (!isLeft)
                return;
            const target = event.target;
            const segmentEl = target?.closest?.(".rec-dropdown__button")?.closest(".transcript-chat-message__segment");
            if (!(segmentEl instanceof HTMLElement))
                return;
            const segmentId = Number(segmentEl.dataset.segmentId);
            const segment = props.segments.find((seg) => seg.id === segmentId);
            if (!segment || segment.words.length === 0)
                return;
            const flatIdx = flatIndex.value.get(`${segmentId}:${segment.words.length - 1}`);
            if (flatIdx == null)
                return;
            event.preventDefault();
            event.stopPropagation();
            focusedFlatIndex.value = flatIdx;
            void nextTick(() => wordEls.value[flatIdx]?.focus());
        };
        // One capture listener fans out to both concerns (ArrowLeft re-entry + Escape refocus) on disjoint keys.
        const onRootCapture = (event) => {
            onMessageKeydownCapture(event);
            onRootKeydownCapture(event);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "rootEl",
                ref: rootEl,
                role: "group",
                tabindex: "0",
                "aria-label": messageAnnouncement.value,
                "aria-busy": __props.regenerating || undefined,
                class: _normalizeClass([
                    'transcript-chat-message',
                    {
                        'transcript-chat-message--regenerating': __props.regenerating,
                        'transcript-chat-message--host': __props.isHost,
                        'transcript-chat-message--backroom': isChatSourced.value && isBackroom.value,
                        'transcript-chat-message--no-text-hover': __props.disableTextHover,
                        'transcript-chat-message--no-word-underline': __props.disableWordHoverUnderline
                    }
                ]),
                onKeydownCapture: onRootCapture
            }, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(hasProfileLink.value ? 'a' : 'div'), {
                    href: hasProfileLink.value ? __props.speakerProfileUrl : undefined,
                    target: hasProfileLink.value ? '_blank' : undefined,
                    rel: hasProfileLink.value ? 'noopener noreferrer' : undefined,
                    "aria-label": hasProfileLink.value ? _ctx.$t('View {0} profile', [__props.speakerDisplayName]) : undefined,
                    class: _normalizeClass([
                        'transcript-chat-message__avatar',
                        {
                            'transcript-chat-message__avatar--circular': __props.circularAvatar,
                            'rc-profile-card': hasProfileLink.value
                        }
                    ]),
                    style: _normalizeStyle({ backgroundColor: __props.speakerColor })
                }, {
                    default: _withCtx(() => [
                        (__props.speakerProfilePhoto)
                            ? (_openBlock(), _createElementBlock("img", {
                                key: 0,
                                class: "transcript-chat-message__avatar-photo",
                                src: __props.speakerProfilePhoto,
                                alt: ""
                            }, null, 8, _hoisted_2))
                            : (_openBlock(), _createBlock(SvgIcon, {
                                key: 1,
                                class: "transcript-chat-message__avatar-icon",
                                icon: "icon-participant-outline"
                            }))
                    ]),
                    _: 1
                }, 8, ["href", "target", "rel", "aria-label", "class", "style"])),
                _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                        (hasProfileLink.value)
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 0,
                                class: _normalizeClass(["transcript-chat-message__speaker-name rc-profile-card rc-name", { 'rc-inactive': __props.speakerInactive }]),
                                href: __props.speakerProfileUrl,
                                target: "_blank",
                                rel: "noopener noreferrer"
                            }, _toDisplayString(__props.speakerDisplayName), 11, _hoisted_5))
                            : (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: _normalizeClass(["transcript-chat-message__speaker-name rc-name", { 'rc-inactive': __props.speakerInactive }]),
                                tabindex: "-1"
                            }, _toDisplayString(__props.speakerDisplayName), 3)),
                        (isChatSourced.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(sourceLabel.value), 1))
                            : _createCommentVNode("", true),
                        (!__props.regenerating)
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 3,
                                class: "transcript-chat-message__segment-start",
                                type: "button",
                                tabindex: "0",
                                "aria-label": _ctx.$t('Play from {0}', [_unref(formatDuration)(__props.segments[0]?.start ?? 0)]),
                                onClick: _cache[0] || (_cache[0] = ($event) => (_ctx.$emit('seek', __props.segments[0]?.start ?? 0)))
                            }, _toDisplayString(_unref(formatDuration)(__props.segments[0]?.start ?? 0)), 9, _hoisted_7))
                            : _createCommentVNode("", true),
                        (isChatSourced.value)
                            ? _withDirectives((_openBlock(), _createElementBlock("span", {
                                key: 4,
                                class: "transcript-chat-message__chat-icon",
                                tabindex: "0",
                                role: "img",
                                "aria-label": chatIconLabel.value
                            }, [
                                _createVNode(SvgIcon, {
                                    icon: "icon-keyboard",
                                    class: "icon-keyboard"
                                })
                            ], 8, _hoisted_8)), [
                                [
                                    _unref(vTooltip),
                                    chatIconLabel.value,
                                    void 0,
                                    { n: true }
                                ]
                            ])
                            : _createCommentVNode("", true),
                        (!__props.regenerating && (__props.editable || __props.disableSpeakerMenuOptions) && __props.speakerLabel && showSpeakerMenu.value)
                            ? (_openBlock(), _createBlock(DropdownMenu, {
                                key: 5,
                                ref_key: "speakerMenu",
                                ref: speakerMenu,
                                "button-tabindex": 0,
                                "button-attrs": { 'aria-label': _ctx.$t('Press Enter for more options.') },
                                lazy: ""
                            }, {
                                button: _withCtx(() => [
                                    _createVNode(SvgIcon, {
                                        icon: "icon-arrow-down",
                                        class: "rec-dropdown__chevron"
                                    })
                                ]),
                                menu: _withCtx(({ toggleMenu }) => [
                                    _renderSlot(_ctx.$slots, "speaker-menu", {
                                        label: __props.speakerLabel,
                                        segmentIds: messageSegmentIds.value,
                                        close: () => closeSpeakerMenu(toggleMenu)
                                    })
                                ]),
                                _: 3
                            }, 8, ["button-attrs"]))
                            : _createCommentVNode("", true)
                    ]),
                    (__props.regenerating)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createVNode(LoadingDots, {
                                label: _ctx.$t('Re-transcribing')
                            }, null, 8, ["label"])
                        ]))
                        : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(segmentUnits.value, (unit) => {
                        return (_openBlock(), _createElementBlock("div", _mergeProps({
                            key: unit[0].id,
                            class: "transcript-chat-message__segments"
                        }, { ref_for: true }, __props.focusAreaKey ? __props.segmentTextAttrs?.(unit[0]) : undefined), [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unit, (segment) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: segment.id,
                                    "data-segment-id": segment.id,
                                    dir: "auto",
                                    class: "transcript-chat-message__segment"
                                }, [
                                    _renderSlot(_ctx.$slots, "segment-decorations", { segment: segment }),
                                    (segment.content)
                                        ? (_openBlock(), _createElementBlock("div", _mergeProps({
                                            key: 0,
                                            class: "transcript-chat-message__segment-text transcript-chat-message__segment-text--mirrored rc-RTEContent"
                                        }, { ref_for: true }, __props.focusAreaKey ? undefined : __props.segmentTextAttrs?.(segment), {
                                            innerHTML: segment.content
                                        }), null, 16, _hoisted_11))
                                        : (segment.words.length)
                                            ? (_openBlock(), _createElementBlock("span", _mergeProps({
                                                key: 1,
                                                class: "transcript-chat-message__segment-text"
                                            }, { ref_for: true }, __props.focusAreaKey ? undefined : __props.segmentTextAttrs?.(segment)), [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(segment.words, (chunk, i) => {
                                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                                        key: `${segment.id}:${i}`
                                                    }, [
                                                        (i > 0)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                _createTextVNode(_toDisplayString(" "))
                                                            ], 64))
                                                            : _createCommentVNode("", true),
                                                        (editing.value && editing.value.segmentId === segment.id && editing.value.wordIndex === i)
                                                            ? (_openBlock(), _createBlock(WordInlineEditor, {
                                                                key: `word-editor-${editSession.value}`,
                                                                text: chunk.word.trim(),
                                                                "word-index": i,
                                                                "select-on-mount": editing.value.selectOnMount,
                                                                onCommit: ($event) => (onCommit(segment, $event)),
                                                                onCancel: onCancel
                                                            }, null, 8, ["text", "word-index", "select-on-mount", "onCommit"]))
                                                            : (_openBlock(), _createElementBlock("span", {
                                                                key: 2,
                                                                ref_for: true,
                                                                ref: (el) => registerWordEl(segment, i, el),
                                                                style: _normalizeStyle(wordStyle(chunk)),
                                                                start: chunk.start,
                                                                end: chunk.end,
                                                                class: _normalizeClass([
                                                                    'transcript-chat-message__word',
                                                                    { 'is-edited-session': __props.editable && !!editedIndicesBySegment.value.get(segment.id)?.has(i) }
                                                                ]),
                                                                "data-editable": __props.editable ? 'true' : undefined,
                                                                "data-word-id": `${segment.id}:${i}`,
                                                                role: "button",
                                                                tabindex: activeWordOf(segment.id) === i ? 0 : -1,
                                                                onClick: ($event) => (onWordClick($event, chunk.start)),
                                                                onDblclick: ($event) => (onWordDblclick(segment.id, i)),
                                                                onFocus: ($event) => (onWordFocus(segment, i)),
                                                                onKeydown: ($event) => (onWordKeydown($event, segment, i, chunk.start))
                                                            }, [
                                                                _renderSlot(_ctx.$slots, "word-decorations", {
                                                                    segment: segment,
                                                                    wordIndex: i,
                                                                    chunk: chunk
                                                                }),
                                                                _createTextVNode(_toDisplayString(chunk.word.trim()), 1)
                                                            ], 46, _hoisted_12)),
                                                        _renderSlot(_ctx.$slots, "word-separator", {
                                                            segment: segment,
                                                            wordIndex: i,
                                                            chunk: chunk
                                                        })
                                                    ], 64));
                                                }), 128))
                                            ], 16))
                                            : _createCommentVNode("", true),
                                    (isChatSourced.value && segment.assets?.length)
                                        ? (_openBlock(), _createBlock(TranscriptAssets, {
                                            key: 2,
                                            assets: segment.assets
                                        }, null, 8, ["assets"]))
                                        : _createCommentVNode("", true),
                                    (isChatSourced.value && segment.poll)
                                        ? (_openBlock(), _createBlock(TranscriptPoll, {
                                            key: 3,
                                            poll: segment.poll
                                        }, null, 8, ["poll"]))
                                        : _createCommentVNode("", true),
                                    (__props.editable && showSegmentMenu.value && !armedSegmentMenus.value.has(segment.id))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                            _createElementVNode("button", {
                                                type: "button",
                                                class: "rec-dropdown__button rec-dropdown__button--custom rec-dropdown__button--lazy-placeholder",
                                                tabindex: "0",
                                                "aria-haspopup": "true",
                                                "aria-label": _ctx.$t('Press Enter for more options.'),
                                                onFocus: ($event) => (armSegmentMenu(segment.id)),
                                                onPointerdown: ($event) => (armSegmentMenu(segment.id, $event))
                                            }, [
                                                _createVNode(SvgIcon, {
                                                    icon: "icon-arrow-down",
                                                    class: "rec-dropdown__chevron"
                                                })
                                            ], 40, _hoisted_14)
                                        ]))
                                        : _createCommentVNode("", true),
                                    (__props.editable && showSegmentMenu.value && armedSegmentMenus.value.has(segment.id))
                                        ? (_openBlock(), _createBlock(DropdownMenu, {
                                            key: 5,
                                            ref_for: true,
                                            ref: (el) => registerSegmentMenu(segment.id, el),
                                            "auto-close": false,
                                            "button-tabindex": 0,
                                            "button-attrs": { 'aria-label': _ctx.$t('Press Enter for more options.') },
                                            class: "transcript-chat-message__segment-menu"
                                        }, {
                                            button: _withCtx(() => [
                                                _createVNode(SvgIcon, {
                                                    icon: "icon-arrow-down",
                                                    class: "rec-dropdown__chevron"
                                                })
                                            ]),
                                            menu: _withCtx(({ toggleMenu }) => [
                                                _renderSlot(_ctx.$slots, "segment-menu", {
                                                    label: __props.speakerLabel,
                                                    segmentId: segment.id,
                                                    indexInMessage: segmentIndexById.value.get(segment.id) ?? 0,
                                                    messageSegmentIds: messageSegmentIds.value,
                                                    close: () => closeSegmentMenu(toggleMenu, segment.id)
                                                })
                                            ]),
                                            _: 2
                                        }, 1032, ["button-attrs"]))
                                        : _createCommentVNode("", true)
                                ], 8, _hoisted_10));
                            }), 128))
                        ], 16));
                    }), 128))
                ])
            ], 42, _hoisted_1));
        };
    }
});
